import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import {
  ARCADE_ACCENT,
  getTriviaFormatLabel,
} from "../../../utils/triviaArcade";

const DIFFICULTIES = ["easy", "medium", "hard"];

// The engine treats a null maxGap as unbounded; the top of the slider stands in
// for that so operators can still reach it.
const UNBOUNDED_GAP = 20;

const FORMAT_MIX_LABELS = {
  daily10: "Daily 10",
  quick5: "Quick 5",
  customCycle: "Custom cycle",
};

const titleCase = (value) => value.charAt(0).toUpperCase() + value.slice(1);
const toPercent = (value) => Math.round((value || 0) * 100);

const SECTIONS = [
  {
    key: "fame",
    title: "Fame window",
    metric: "fame rank",
    min: 1,
    max: 100,
    toSlider: (row) => [row.minRank, row.maxRank],
    fromSlider: ([low, high]) => ({ minRank: low, maxRank: high }),
    describe: (row) => `${row.minRank}–${row.maxRank}`,
  },
  {
    key: "higherLower",
    title: "Higher / lower",
    metric: "HL gap",
    min: 1,
    max: UNBOUNDED_GAP,
    toSlider: (row) => [
      row.minGap,
      row.maxGap == null ? UNBOUNDED_GAP : row.maxGap,
    ],
    fromSlider: ([low, high]) => ({
      minGap: low,
      maxGap: high >= UNBOUNDED_GAP ? null : high,
    }),
    describe: (row) =>
      row.maxGap == null ? `${row.minGap}+` : `${row.minGap}–${row.maxGap}`,
  },
  {
    key: "trueFalse",
    title: "True / false",
    metric: "TF distance",
    min: 1,
    max: 40,
    toSlider: (row) => [toPercent(row.minPct), toPercent(row.maxPct)],
    fromSlider: ([low, high]) => ({ minPct: low / 100, maxPct: high / 100 }),
    describe: (row) => `${toPercent(row.minPct)}–${toPercent(row.maxPct)}%`,
  },
];

const countFormats = (formats = []) =>
  formats.reduce((acc, format) => {
    const existing = acc.find((entry) => entry.format === format);
    if (existing) existing.count += 1;
    else acc.push({ format, count: 1 });
    return acc;
  }, []);

const TriviaPresetRail = ({
  draft,
  active,
  dirty,
  onDraftChange,
  onSave,
  isSaving,
  error,
}) => {
  if (!draft) {
    return (
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography color="text.secondary">Loading active preset…</Typography>
      </Stack>
    );
  }

  const handleSlider = (section, difficulty, value) => {
    onDraftChange({
      ...draft,
      [section.key]: {
        ...draft[section.key],
        [difficulty]: {
          ...draft[section.key][difficulty],
          ...section.fromSlider(value),
        },
      },
    });
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      {SECTIONS.map((section, sectionIndex) => (
        <Box key={section.key}>
          {sectionIndex > 0 && <Divider sx={{ mb: 2 }} />}
          <Typography variant="body2" fontWeight={800} gutterBottom>
            {section.title}
          </Typography>
          <Stack spacing={1.5}>
            {DIFFICULTIES.map((difficulty) => {
              const draftRow = draft[section.key]?.[difficulty];
              const activeRow = active?.[section.key]?.[difficulty];
              if (!draftRow) return null;

              return (
                <Box key={difficulty}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography variant="caption" fontWeight={700}>
                      {titleCase(difficulty)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {section.describe(draftRow)}
                    </Typography>
                  </Box>
                  <Slider
                    size="small"
                    value={section.toSlider(draftRow)}
                    min={section.min}
                    max={section.max}
                    valueLabelDisplay="auto"
                    getAriaLabel={(index) =>
                      `${titleCase(difficulty)} ${section.metric} ${
                        index === 0 ? "minimum" : "maximum"
                      }`
                    }
                    onChange={(_event, value) =>
                      handleSlider(section, difficulty, value)
                    }
                    sx={{ color: ARCADE_ACCENT }}
                  />
                  {activeRow && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="p"
                    >
                      {`${titleCase(difficulty)} ${section.metric} ${section.describe(
                        activeRow
                      )}`}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}

      <Divider />

      <Box>
        <Typography variant="body2" fontWeight={800} gutterBottom>
          Format mix
        </Typography>
        <Stack spacing={1}>
          {Object.entries(draft.formatMix || {}).map(([mixKey, formats]) => (
            <Box key={mixKey}>
              <Typography variant="caption" color="text.secondary">
                {FORMAT_MIX_LABELS[mixKey] || mixKey}
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ flexWrap: "wrap", rowGap: 0.5 }}
              >
                {countFormats(formats).map(({ format, count }) => (
                  <Chip
                    key={format}
                    size="small"
                    variant="outlined"
                    label={
                      count > 1
                        ? `${getTriviaFormatLabel(format)} ×${count}`
                        : getTriviaFormatLabel(format)
                    }
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          component="p"
          sx={{ mt: 1 }}
        >
          Read-only here, still saved with the preset.
        </Typography>
      </Box>

      <Button
        variant={dirty ? "contained" : "outlined"}
        disabled={!dirty || isSaving}
        onClick={onSave}
        sx={{ fontWeight: 800 }}
      >
        Save & activate
      </Button>
    </Stack>
  );
};

export default TriviaPresetRail;
